import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/natural-gas-utilities.webp";
import HeroImgMobile       from "../../assets/images/blogs/natural-gas-utilities-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";

/* ─────────────────────────────────────────────
   HowNaturalGasUtilitiesPlayARoleInTheFutureOfOurEnergySystem.jsx
   Prefix      : ng-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title : LEFT-aligned (like EV Trends page)
   - Newsletter : prop-based isMobile, Subscribe button
   - No tweet images, no inline images
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .ng-wrap *, .ng-wrap *::before, .ng-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .ng-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .ng-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .ng-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .ng-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .ng-fade-up.ng-in { opacity: 1; transform: translateY(0); }
  .ng-delay-1 { transition-delay: 0.08s; }
  .ng-delay-2 { transition-delay: 0.18s; }
  .ng-delay-3 { transition-delay: 0.28s; }
  .ng-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — left-aligned title
     title inside ng-inner for padding
  ══════════════════════════════════════ */
  .ng-hero { width: 100%; background: #1A1A1A; }
  .ng-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .ng-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: left;
  }
  .ng-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .ng-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .ng-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  .ng-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  .ng-subhead {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #ffffff;
    line-height: 160%;
    margin-bottom: 14px;
    margin-top: 44px;
  }

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .ng-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .ng-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
   
    object-position: center;
    z-index: 0;
  }
  .ng-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .ng-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .ng-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .ng-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .ng-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .ng-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .ng-newsletter__input::placeholder { color: #FFFFFF; }
  .ng-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .ng-newsletter__btn:hover { background: #5e2e90; }
  .ng-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .ng-newsletter__btn:hover .ng-newsletter__btn-arrow { transform: translateX(4px); }
  .ng-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .ng-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .ng-inner { padding-left: 32px; padding-right: 32px; }
    .ng-hero__img-wrap img { max-height: 350px; }
    .ng-hero__title-bar { padding: 24px 0px 20px; }
    .ng-hero__title { font-size: 24px; }
    .ng-body-section { padding: 40px 0 48px; }
    .ng-para { margin-bottom: 18px; font-size: 15px; }
    .ng-subhead { font-size: 18px; margin-top: 28px; margin-bottom: 10px; }

    .ng-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .ng-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .ng-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .ng-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .ng-newsletter__heading { font-size: 24px; }
    .ng-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .ng-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .ng-newsletter__input { border-radius: 5px; height: 44px; }
    .ng-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .ng-inner { padding-left: 16px; padding-right: 16px; }
    .ng-hero__title-bar { padding: 30px 0px; }
    .ng-body-section { padding: 30px 0 30px; }
    .ng-hero__img-wrap img { max-height: 366px; width: 100%; }
    .ng-hero__title { font-size: 26px; line-height: 120%; }
    .ng-para { font-size: 14px; margin-bottom: 14px; }
    .ng-subhead { margin-top: 30px; margin-bottom: 14px; font-size: 16px; }

    .ng-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .ng-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .ng-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .ng-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .ng-newsletter__heading { font-size: 30px; line-height: 120%; }
    .ng-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .ng-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .ng-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .ng-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".ng-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("ng-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="ng-newsletter">
      {!isMobile && (
        <>
          <img className="ng-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="ng-newsletter__overlay" />
        </>
      )}
      <div className="ng-newsletter__inner">
        {isMobile && (
          <img className="ng-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="ng-newsletter__content">
          <h2 className="ng-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="ng-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="ng-newsletter__form">
            <input
              className="ng-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="ng-newsletter__btn">
              Subscribe
              <span className="ng-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function HowNaturalGasUtilitiesPlayARoleInTheFutureOfOurEnergySystem() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="ng-wrap">

        {/* HERO — title inside ng-inner, left-aligned */}
        <section className="ng-hero">
          <div className="ng-hero__title-bar">
            <div className="ng-inner">
              <h1 className="ng-hero__title">How Natural Gas Utilities Play a Role in the Future of Our Energy System</h1>
            </div>
          </div>
          <div className="ng-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="Natural Gas Utilities"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="ng-body-section">
          <div className="ng-inner">
            <div className="ng-content-col">

              <p className="ng-para ng-fade-up ng-delay-1">With a global pandemic and unprecedented extreme weather, we have seen how essential our energy delivery system is to our way of life. Through collaboration and innovation, we will make the system more resilient and fulfill our commitment to reducing emissions. American Gas Association members are already leading in these areas—providing customers with reliable and affordable energy 24/7/365 as we reimagine our industry for a cleaner future.</p>

              <p className="ng-para ng-fade-up ng-delay-1">We believe that energy system resilience will be achieved through collaboration—a diverse set of integrated assets working together—rather than reliance on one source.</p>

              <h2 className="ng-subhead ng-fade-up ng-delay-1">Commentary</h2>

              <p className="ng-para ng-fade-up ng-delay-1">A recent study by the American Gas Foundation illustrates why the gas delivery system must be part of that energy mix. The study demonstrates how resiliency is largely derived from the gas system's ability to quickly respond to events and use its extensive storage resources to supply uninterrupted energy for long durations and service peak seasonal demand.</p>

              <p className="ng-para ng-fade-up ng-delay-1">For example, NW Natural in Oregon has 20 billion cubic feet of underground storage today. That's equivalent to about 6 million megawatt-hours of renewable storage capacity. In today's costs, that would be about a $2 trillion battery. That kind of storage capacity—to move energy across days, weeks, months, even seasons—is invaluable.</p>

              <p className="ng-para ng-fade-up ng-delay-1">During early February 2014, a polar vortex brought extreme cold temperatures, snowfall, and high winds to Oregon. On Feb. 6 of that year, during the system peak, NW Natural set a company record for natural gas delivery, which still stands today. This is just one of many examples of the critical role that natural gas storage plays in meeting demand during extreme weather events. Natural gas serves as a baseload electric generation fuel and, with the help of storage resources, can be used to quickly ramp up electric generation needed to address the intermittency challenges of renewable electricity sources. Gas storage meets consumer energy needs today and can be a critical resource to store renewable gases derived from renewable electricity generation far into the future.</p>

              <p className="ng-para ng-fade-up ng-delay-1">The industry is also collaborating on other innovative solutions to improve resilience, including self-powered gas furnaces and water heaters that can generate electricity and allow homes to be less reliant on the electrical grid in an emergency. Organizations like the Gas Technology Institute and Oak Ridge National Laboratory are developing these new natural gas products with resilience, efficiency and affordability top of mind.</p>

              <p className="ng-para ng-fade-up ng-delay-1">By advancing state-of-the-art energy solutions across the entire energy industry, we have an opportunity to build not only a more resilient energy system, but a cleaner one as well. The push toward one does not have to mean sacrificing the other. It will take all of us, and government, working together, to reduce emissions.</p>

              <p className="ng-para ng-fade-up ng-delay-1">The natural gas industry is "all in." Investments to modernize the gas distribution system have reduced methane emissions by 73% since 1990. That is equivalent to removing almost 7 million cars from the road. We are focusing on new opportunities like renewable natural gas (RNG) and zero carbon hydrogen to help us continue driving down emissions while using existing natural gas infrastructure.</p>

              <p className="ng-para ng-fade-up ng-delay-1">RNG can be produced from farms, landfills, water resource recovery facilities or from renewable electricity. Through RNG, we are capturing methane that would normally be released into the atmosphere. This carbon neutral, versatile option is fully compatible with the U.S. pipeline system, making it accessible for homes, businesses and heavy industries like manufacturing. Utilities throughout the country are making significant investments in RNG and some states, such as Oregon, have enacted policies supporting this clean technology.</p>

              <p className="ng-para ng-fade-up ng-delay-1">Another opportunity is zero carbon hydrogen which is produced by hydroelectric power, wind turbines or at nuclear energy plants and can be blended with methane in our existing gas pipeline system. We are working to determine the levels of hydrogen that can be blended into the system while maintaining the safe delivery and use of the fuel. United States Special Presidential Envoy for Climate John Kerry spoke at a recent energy conference about how hydrogen is a critical component of our nation's ability to reach ambitious greenhouse gas reductions goals.</p>

              <p className="ng-para ng-fade-up ng-delay-1">The natural gas industry has proven time-and-time-again how we come together, work hard, innovate and get things done. Our collaboration and innovation to help build a cleaner and more resilient energy system is no different. We will work across state lines and party lines to fulfill our nation's energy needs and achieve our shared environmental goals.</p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}